import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import awsHeader from '../helpers/awsAuth';
import { getErrorMessage } from '../helpers/errors';
import k2aBaseApi from '../apis/key2act';
import useIsMounted from './useIsMounted';

const CancelNoOp = () => {};
const DEFAULT_RESPONSE = Object.freeze({
    cancel:   CancelNoOp,
    complete: false,
    data:     null,
    error:    null,
    loading:  false,
    meta:     null,
    saving:   false
});
const LOADING_METHODS = ['DELETE', 'GET'];
const SAVING_METHODS = ['PATCH', 'POST', 'PUT'];

export default function useApi(fn, baseApi) {
    const isMounted = useIsMounted();
    const signal = useRef(null);
    const [response, setResponse] = useState({ ...DEFAULT_RESPONSE });
    const [request, setRequest] = useState();

    useEffect(() => {
        if (!request) {
            return;
        }
        if (response.loading && signal.current) {
            // Cancel any previous requests
            signal.current.cancel();
        }
        signal.current = axios.CancelToken.source();
        const loading = LOADING_METHODS.includes(request.method);
        const saving = SAVING_METHODS.includes(request.method);
        setResponse({
            ...DEFAULT_RESPONSE,
            cancel: signal.current.cancel,
            loading,
            saving
        });

        async function runFetch() {
            try {
                const activeBaseApi = baseApi ? baseApi : k2aBaseApi;
                const { adapter = activeBaseApi, ...requestProps } = request;
                const awsAuth = await awsHeader();
                const resp = await adapter({
                    cancelToken: signal.current.token,
                    ...requestProps,
                    headers:     {
                        ...awsAuth.headers,
                        ...request.headers
                    }
                });
                if (!isMounted.current) {
                    return;
                }
                setResponse({
                    ...DEFAULT_RESPONSE,
                    complete: true,
                    loading:  false,
                    ...resp.data
                });
            } catch (error) {
                setResponse({
                    ...DEFAULT_RESPONSE,
                    complete: true,
                    error:    getErrorMessage(error)
                });
            }
        }
        runFetch();
    }, [request]);

    return [
        response,
        (...args) => setRequest({
            method: 'GET',
            ...fn(...args)
        })
    ];
}
